import { TabPanel, TabPanelProps } from "../../components/TabPanel.tsx";
import { useEffect, useState } from "react";
import { IUserSession } from "../../../../src/core/types/session/user-session.interface";
import { fetchWithError } from "../../utils/fetchWithError.ts";
import {
    alpha,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
    CircularProgress,
    useMediaQuery,
    useTheme
} from "@mui/material";
import { 
    ArrowForward, 
    Close,
    ContentCopy, 
    Security, 
    Badge, 
    Key,
    Visibility,
    VisibilityOff,
    Delete,
    Warning,
    History
} from "@mui/icons-material";
import { useSnackbar } from 'notistack';
import { PopulatedAccount } from "../../../../src/core/types/user/account.interface";
import { ISecurityInformation } from "../../../../src/core/types/core/security-information.interface";
import { AnimatedPasswordCheck } from "../../components/PasswordCheck.tsx";
import { isStrongPassword } from "../../../../src/shared";

export const AccountTab = (props: TabPanelProps) => {
    const [session, setSession] = useState<IUserSession>();
    const [account, setAccount] = useState<PopulatedAccount>();
    const [securityInfo, setSecurityInfo] = useState<ISecurityInformation | null>(null);
    const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
    const [showPasswordHint, setShowPasswordHint] = useState(false);
    const [isUnlinking, setIsUnlinking] = useState(false);
    const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);
    const [deletionEmail, setDeletionEmail] = useState("");
    const [appUsers, setAppUsers] = useState<any[]>([]);
    const [isRequestingDeletion, setIsRequestingDeletion] = useState(false);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        loadSession();
    }, []);

    const loadSession = async () => {
        setLoading(true);
        try {
            const [sessionRes, accountRes, securityRes, appUsersRes] = await Promise.all([
                fetchWithError("/session", { credentials: "include" }).catch(() => null),
                fetchWithError("/session/account", { credentials: "include" }).catch(() => null),
                fetchWithError("/session/security-info", { credentials: "include" }).catch(() => null),
                fetchWithError("/session/app-users", { credentials: "include" }).catch(() => [])
            ]);

            if (sessionRes?.session) {
                setSession(sessionRes.session);
            }
            if (accountRes) {
                setAccount(accountRes);
            }
            if (securityRes) {
                setSecurityInfo(securityRes);
            }
            if (appUsersRes) {
                setAppUsers(appUsersRes);
            }
        } catch (error) {
            console.error("Error loading session data:", error);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text)
            .then(() => enqueueSnackbar("In Zwischenablage kopiert!", { variant: 'info' }));
    };

    const handleUnlinkGoogle = async () => {
        setIsUnlinking(true);
        try {
            // Validate password strength
            const pwd = isStrongPassword(password);
            if (!pwd.isStrongPassword) {
                setShowPasswordHint(true);
                enqueueSnackbar("Das Passwort erfüllt nicht die Anforderungen!", { variant: 'warning' });
                setIsUnlinking(false);
                return;
            }

            // Validate password confirmation
            if (password !== passwordConfirmation) {
                enqueueSnackbar("Die Passwörter stimmen nicht überein!", { variant: 'error' });
                setIsUnlinking(false);
                return;
            }

            // Call API
            await fetchWithError("/session/unlink-google", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                credentials: "include",
                body: JSON.stringify({
                    password,
                    passwordConfirmation
                })
            });

            enqueueSnackbar("Google-Verknüpfung wurde erfolgreich aufgehoben!", { variant: 'success' });
            setUnlinkDialogOpen(false);
            setPassword("");
            setPasswordConfirmation("");
            setShowPasswordHint(false);
            // Reload account data
            loadSession();
        } catch (error) {
            if (error instanceof Error) {
                enqueueSnackbar(error.message, { variant: 'error' });
            } else {
                enqueueSnackbar("Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie den Vorgang erneut.", { variant: 'error' });
            }
        } finally {
            setIsUnlinking(false);
        }
    };

    const handleCloseUnlinkDialog = () => {
        setUnlinkDialogOpen(false);
        setPassword("");
        setPasswordConfirmation("");
        setShowPasswordHint(false);
    };

    const handleOpenDeletionDialog = () => {
        setDeletionDialogOpen(true);
    };

    const handleCloseDeletionDialog = () => {
        setDeletionDialogOpen(false);
        setDeletionEmail("");
    };

    const handleRequestDeletion = async () => {
        if (!deletionEmail || !deletionEmail.trim()) {
            enqueueSnackbar("Bitte geben Sie Ihre E-Mail-Adresse ein.", { variant: 'error' });
            return;
        }

        setIsRequestingDeletion(true);
        try {
            await fetchWithError("/session/request-deletion", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ email: deletionEmail.trim() }),
                credentials: "include"
            });

            enqueueSnackbar("Eine E-Mail mit Bestätigungslink wurde an Sie gesendet.", { variant: 'success' });
            setDeletionDialogOpen(false);
            setDeletionEmail("");
        } catch (error) {
            if (error instanceof Error) {
                enqueueSnackbar(error.message, { variant: 'error' });
            } else {
                enqueueSnackbar("Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie den Vorgang erneut.", { variant: 'error' });
            }
        } finally {
            setIsRequestingDeletion(false);
        }
    };

    if (loading) {
        return (
            <TabPanel {...props}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 600 }}>
                    <CircularProgress />
                </Box>
            </TabPanel>
        );
    }

    if (!session) {
        return (
            <TabPanel {...props}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 600 }}>
                    <Typography color="text.secondary">Lade Kontodaten...</Typography>
                </Box>
            </TabPanel>
        );
    }

    return (
        <TabPanel {...props}>
            <Stack spacing={3}>
                {/* Always visible: Identification and Security */}
                <Box sx={{ 
                    display: 'grid', 
                    gap: { xs: 2, sm: 3 }, 
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
                }}>
                <Card 
                    variant="subtle"
                    sx={{ 
                        height: '100%', 
                        borderRadius: { xs: 0, sm: 2 }
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Badge sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="h6">Identifikation</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Deine tintoa ID
                                </Typography>
                                <Stack 
                                    direction={{ xs: "column", sm: "row" }} 
                                    alignItems={{ xs: "flex-start", sm: "center" }} 
                                    spacing={1}
                                >
                                    <Typography 
                                        variant="body1" 
                                        sx={{ 
                                            fontFamily: 'monospace',
                                            wordBreak: 'break-all'
                                        }}
                                    >
                                        {session.tid}
                                    </Typography>
                                    <Tooltip title="ID kopieren">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => copyToClipboard(session.tid)}
                                            sx={{ mt: { xs: 1, sm: 0 } }}
                                        >
                                            <ContentCopy fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                <Card 
                    variant="subtle"
                    sx={{ 
                        height: '100%', 
                        borderRadius: { xs: 0, sm: 2 }
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Security sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="h6">Sicherheit</Typography>
                            </Box>
                            {account?.isGoogleLinked && (
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        gap: 1,
                                        p: 1,
                                        mx: -1
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ 
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}
                                        >
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 18 18"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g fill="none" fillRule="evenodd">
                                                    <path
                                                        d="M17.64 9.2045c0-.6371-.0573-1.2516-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7955 2.7164v2.2581h2.9087c1.7023-1.5668 2.6836-3.8741 2.6836-6.6146z"
                                                        fill={theme.palette.semantic!.brand.google.blue}
                                                    />
                                                    <path
                                                        d="M9 18c2.4295 0 4.4673-.7955 5.9564-2.1805l-2.9087-2.2581c-.8046.54-1.8364.8591-3.0477.8591-2.3441 0-4.3273-1.5836-5.0382-3.7105H.9573v2.3318C2.4382 15.9836 5.4818 18 9 18z"
                                                        fill={theme.palette.semantic!.brand.google.green}
                                                    />
                                                    <path
                                                        d="M3.9614 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9965 8.9965 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418l3.0041-2.3318z"
                                                        fill={theme.palette.semantic!.brand.google.yellow}
                                                    />
                                                    <path
                                                        d="M9 3.5795c1.3214 0 2.5082.4545 3.4409 1.3455l2.5818-2.5818C12.4636.8918 10.4255 0 9 0 5.4818 0 2.4382 2.0164.9573 4.9582L3.9614 7.29C4.6723 5.1636 6.6555 3.5795 9 3.5795z"
                                                        fill={theme.palette.semantic!.brand.google.red}
                                                    />
                                                </g>
                                            </svg>
                                            Dieses Konto ist mit Google verknüpft
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        onClick={() => setUnlinkDialogOpen(true)}
                                        sx={{ alignSelf: 'flex-start' }}
                                    >
                                        Verknüpfung aufheben
                                    </Button>
                                </Box>
                            )}
                            {!account?.isGoogleLinked && (
                                <>
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                            borderRadius: 1
                                        },
                                        p: 1,
                                        mx: -1
                                    }}
                                    onClick={() => window.location.href = '/app/change-password'}
                                >
                                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                                        <Typography variant="body1">Passwort ändern</Typography>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ 
                                                display: { xs: 'none', sm: 'block' }
                                            }}
                                        >
                                            Regelmäßige Änderungen erhöhen die Sicherheit
                                        </Typography>
                                    </Stack>
                                    <ArrowForward color="action" />
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        p: 1,
                                        mx: -1
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 18 18"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g fill="none" fillRule="evenodd">
                                                <path
                                                    d="M17.64 9.2045c0-.6371-.0573-1.2516-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7955 2.7164v2.2581h2.9087c1.7023-1.5668 2.6836-3.8741 2.6836-6.6146z"
                                                    fill={theme.palette.semantic!.brand.google.blue}
                                                />
                                                <path
                                                    d="M9 18c2.4295 0 4.4673-.7955 5.9564-2.1805l-2.9087-2.2581c-.8046.54-1.8364.8591-3.0477.8591-2.3441 0-4.3273-1.5836-5.0382-3.7105H.9573v2.3318C2.4382 15.9836 5.4818 18 9 18z"
                                                    fill={theme.palette.semantic!.brand.google.green}
                                                />
                                                <path
                                                    d="M3.9614 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9965 8.9965 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418l3.0041-2.3318z"
                                                    fill={theme.palette.semantic!.brand.google.yellow}
                                                />
                                                <path
                                                    d="M9 3.5795c1.3214 0 2.5082.4545 3.4409 1.3455l2.5818-2.5818C12.4636.8918 10.4255 0 9 0 5.4818 0 2.4382 2.0164.9573 4.9582L3.9614 7.29C4.6723 5.1636 6.6555 3.5795 9 3.5795z"
                                                    fill={theme.palette.semantic!.brand.google.red}
                                                />
                                            </g>
                                        </svg>
                                        Dieses Konto ist nicht mit Google verknüpft
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Nach der Verknüpfung meldest du dich über Google an. Die Google-E-Mail muss mit diesem Konto übereinstimmen.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        onClick={() => {
                                            window.location.href = '/auth/google/link';
                                        }}
                                        sx={{ alignSelf: 'flex-start' }}
                                    >
                                        Mit Google verknüpfen
                                    </Button>
                                </Box>
                                </>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
                </Box>

                {/* Account sections */}
                <Box>
                    {/* Roles & Permissions */}
                    {account && account.roles.length > 0 && (
                        <Card
                            variant="subtle"
                            sx={{
                                borderRadius: { xs: 0, sm: 2 }
                            }}
                        >
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Key sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="h6">Rollen & Berechtigungen</Typography>
                                </Box>
                                <Stack 
                                    direction="row" 
                                    spacing={1} 
                                    flexWrap="wrap" 
                                    useFlexGap 
                                    sx={{ gap: 1 }}
                                >
                                    {account.roles.map((r, i) => (
                                        <Chip 
                                            key={`role_${i}`} 
                                            label={r.name}
                                            size={isMobile ? "small" : "medium"}
                                            sx={{ 
                                                bgcolor: 'primary.main',
                                                color: 'primary.contrastText'
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    {/* Security Details */}
                    {securityInfo && (
                        <Card
                            variant="subtle"
                            sx={{
                                borderRadius: { xs: 0, sm: 2 },
                                mt: 2
                            }}
                        >
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Security sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="h6">Sicherheitsdetails</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Alle sicherheitsrelevanten Informationen zu deinem Account
                                    </Typography>
                                    <Stack spacing={1} sx={{ mt: 2 }}>
                                        <Typography variant="body2"><b>Letzter Login:</b> {securityInfo.lastLogin ? new Date(securityInfo.lastLogin).toLocaleString() : '-'}</Typography>
                                        <Typography variant="body2"><b>Gerät beim letzten Login:</b> {securityInfo.lastLoginDevice || '-'}</Typography>
                                        <Typography variant="body2"><b>IP beim letzten Login:</b> {securityInfo.lastLoginIp || '-'}</Typography>
                                        <Typography variant="body2"><b>Letzte Passwortänderung:</b> {securityInfo.lastPasswordChange ? new Date(securityInfo.lastPasswordChange).toLocaleString() : '-'}</Typography>
                                        <Typography variant="body2"><b>Fehlgeschlagene Login-Versuche:</b> {securityInfo.failedLoginAttempts ?? '-'}</Typography>
                                        <Typography variant="body2"><b>Letzter fehlgeschlagener Login:</b> {securityInfo.lastFailedLogin ? new Date(securityInfo.lastFailedLogin).toLocaleString() : '-'}</Typography>
                                        <Typography variant="body2"><b>Gerät beim letzten fehlgeschlagenen Login:</b> {securityInfo.lastFailedLoginDevice || '-'}</Typography>
                                        <Typography variant="body2"><b>IP beim letzten fehlgeschlagenen Login:</b> {securityInfo.lastFailedLoginIp || '-'}</Typography>
                                    </Stack>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Login History */}
                    {securityInfo && (
                        <Card
                            variant="subtle"
                            sx={{
                                borderRadius: { xs: 0, sm: 2 },
                                mt: 2
                            }}
                        >
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <History sx={{ color: 'primary.main', mr: 1 }} />
                                    <Typography variant="h6">Login-Historie</Typography>
                                </Box>
                                <Box>
                                    {securityInfo.loginHistory && securityInfo.loginHistory.length > 0 ? (
                                        <Box sx={{ maxHeight: 300, overflow: 'auto', border: 0, borderRadius: 1, p: 1 }}>
                                            <table style={{ width: '100%', fontSize: '0.95em' }}>
                                                <thead>
                                                    <tr>
                                                        <th>Datum</th>
                                                        <th>Gerät</th>
                                                        <th>IP</th>
                                                        <th>Client</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {securityInfo.loginHistory.map((entry, idx) => (
                                                        <tr key={idx}>
                                                            <td>{entry.date ? new Date(entry.date).toLocaleString() : '-'}</td>
                                                            <td>{entry.device || '-'}</td>
                                                            <td>{entry.ip || '-'}</td>
                                                            <td>{entry.clientApplication?.name || '-'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">Keine Login-Historie vorhanden.</Typography>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Box>

                {/* Account Deletion Section - Always visible at bottom */}
                <Card 
                    elevation={0} 
                    sx={{ 
                        background: (t) => alpha(t.palette.error.main, t.palette.mode === 'dark' ? t.palette.semantic!.errorBackgroundAlpha.dark : t.palette.semantic!.errorBackgroundAlpha.light),
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: { xs: 0, sm: 2 },
                        mt: 3
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Warning sx={{ color: 'error.main', mr: 1 }} />
                                <Typography variant="h6" color="error.main">
                                    Gefährlicher Bereich
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Wenn du deinen Zugang löschst, verlierst du den Zugang zu allen tintoa Anwendungen und alle deine Daten gehen unwiderruflich verloren.
                            </Typography>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<Delete />}
                                onClick={handleOpenDeletionDialog}
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                Zugang löschen
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>

            {/* Unlink Google Account Dialog */}
            <Dialog
                open={unlinkDialogOpen}
                onClose={handleCloseUnlinkDialog}
                aria-labelledby="unlink-dialog-title"
                aria-describedby="unlink-dialog-description"
                PaperProps={{
                    elevation: 0,
                    sx: {
                        borderRadius: 2,
                        border: 1,
                        borderColor: "divider",
                        m: { xs: 2, sm: 4 },
                        width: { xs: "100%", sm: "500px" }
                    }
                }}
                fullScreen={isMobile}
            >
                <DialogTitle
                    id="unlink-dialog-title"
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pb: 1,
                        pt: { xs: 2, sm: 2 }
                    }}
                >
                    <Typography variant="h6">
                        Google-Verknüpfung aufheben
                    </Typography>
                    <IconButton
                        onClick={handleCloseUnlinkDialog}
                        size="small"
                        sx={{
                            color: "text.secondary",
                            "&:hover": { color: "text.primary" }
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="unlink-dialog-description"
                        sx={{ py: 1, mb: 2 }}
                    >
                        Dein Account wird von Google getrennt und verwandelt sich in ein eigenständiges tintoa Konto. 
                        Dafür musst du ein neues Passwort für dein Konto setzen.
                        Du kannst die Verknüpfung jederzeit wiederherstellen, indem du unter Sicherheit „Mit Google verknüpfen“ nutzt oder dich erneut mit Google anmeldest.
                    </DialogContentText>
                    <Stack spacing={2}>
                        <TextField
                            label="Neues Passwort"
                            variant="outlined"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                if (!showPasswordHint) {
                                    setShowPasswordHint(true);
                                }
                            }}
                            required
                            fullWidth
                            autoFocus
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <AnimatedPasswordCheck
                            show={showPasswordHint && !isStrongPassword(password).isStrongPassword}
                            password={password}
                        />
                        <TextField
                            label="Passwort bestätigen"
                            variant="outlined"
                            type={showPasswordConfirmation ? "text" : "password"}
                            value={passwordConfirmation}
                            onChange={(e) => setPasswordConfirmation(e.target.value)}
                            required
                            fullWidth
                            error={password !== passwordConfirmation && passwordConfirmation.length > 0}
                            helperText={password !== passwordConfirmation && passwordConfirmation.length > 0 ? "Passwörter stimmen nicht überein" : ""}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password confirmation visibility"
                                            onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                                            edge="end"
                                        >
                                            {showPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        pb: { xs: 2, sm: 2 },
                        flexDirection: { xs: "column", sm: "row" },
                        gap: { xs: 1, sm: 1 },
                        "& .MuiButton-root": {
                            width: { xs: "100%", sm: "auto" }
                        }
                    }}
                >
                    <Button
                        variant={isMobile ? "outlined" : "text"}
                        size={isMobile ? "large" : "medium"}
                        onClick={handleCloseUnlinkDialog}
                        fullWidth={isMobile}
                        disabled={isUnlinking}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size={isMobile ? "large" : "medium"}
                        onClick={handleUnlinkGoogle}
                        autoFocus
                        fullWidth={isMobile}
                        disabled={isUnlinking || !password || !passwordConfirmation}
                    >
                        {isUnlinking ? "Wird verarbeitet..." : "Verknüpfung aufheben"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Deletion Confirmation Dialog */}
            <Dialog
                open={deletionDialogOpen}
                onClose={handleCloseDeletionDialog}
                aria-labelledby="deletion-dialog-title"
                aria-describedby="deletion-dialog-description"
                PaperProps={{
                    elevation: 0,
                    sx: {
                        borderRadius: 2,
                        border: 1,
                        borderColor: "error.main",
                        m: { xs: 2, sm: 4 },
                        width: { xs: "100%", sm: "500px" }
                    }
                }}
                fullScreen={isMobile}
            >
                <DialogTitle
                    id="deletion-dialog-title"
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pb: 1,
                        pt: { xs: 2, sm: 2 },
                        color: "error.main"
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning />
                        <Typography variant="h6">
                            Zugang löschen
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleCloseDeletionDialog}
                        size="small"
                        sx={{
                            color: "text.secondary",
                            "&:hover": { color: "text.primary" }
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="deletion-dialog-description"
                        sx={{ py: 1, mb: 2 }}
                    >
                        <Typography variant="body1" paragraph sx={{ color: 'error.main', fontWeight: 'bold' }}>
                            Achtung: Diese Aktion kann nicht rückgängig gemacht werden!
                        </Typography>
                        <Typography variant="body2" paragraph>
                            Wenn du deinen Zugang löschst:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                            <li>Verlierst du den Zugang zu allen tintoa Anwendungen</li>
                            <li>Werden alle deine Daten unwiderruflich gelöscht</li>
                            <li>Kannst du dich nicht mehr mit diesem Account anmelden</li>
                        </Box>
                        {appUsers.length > 0 && (
                            <Box sx={{ mt: 2, mb: 2 }}>
                                <Typography variant="body2" fontWeight="bold" gutterBottom>
                                    Betroffene Anwendungen:
                                </Typography>
                                <Box component="ul" sx={{ pl: 2 }}>
                                    {appUsers.map((appUser) => (
                                        appUser.clientApp && (
                                            <li key={appUser.id}>
                                                <Typography variant="body2">
                                                    {appUser.clientApp.name}
                                                </Typography>
                                            </li>
                                        )
                                    ))}
                                </Box>
                            </Box>
                        )}
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Wenn du fortfährst, senden wir dir eine E-Mail mit einem Bestätigungslink, um die Löschung abzuschließen.
                        </Typography>
                    </DialogContentText>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="E-Mail-Adresse"
                            type="email"
                            value={deletionEmail}
                            onChange={(e) => setDeletionEmail(e.target.value)}
                            placeholder="ihre.email@example.com"
                            disabled={isRequestingDeletion}
                            required
                            autoFocus
                            sx={{ mt: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Bitte geben Sie die E-Mail-Adresse ein, die mit Ihrem Account verknüpft ist.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        pb: { xs: 2, sm: 2 },
                        flexDirection: { xs: "column", sm: "row" },
                        gap: { xs: 1, sm: 1 },
                        "& .MuiButton-root": {
                            width: { xs: "100%", sm: "auto" }
                        }
                    }}
                >
                    <Button
                        variant={isMobile ? "outlined" : "text"}
                        size={isMobile ? "large" : "medium"}
                        onClick={handleCloseDeletionDialog}
                        fullWidth={isMobile}
                        disabled={isRequestingDeletion}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        size={isMobile ? "large" : "medium"}
                        onClick={handleRequestDeletion}
                        autoFocus
                        fullWidth={isMobile}
                        disabled={isRequestingDeletion}
                        startIcon={<Delete />}
                    >
                        {isRequestingDeletion ? "Wird verarbeitet..." : "Fortfahren"}
                    </Button>
                </DialogActions>
            </Dialog>
        </TabPanel>
    );
};
